import { useMemo } from 'react';
import type { ProjectSettingsPage } from '@atlassian/jira-forge-ui-types';
import { useDoesExtensionMeetConditions } from '@atlassian/jira-forge-ui-utils/src/utils/conditions';
import { parseExtensionId } from '@atlassian/jira-forge-ui-utils/src/utils/parse-extension-id';
import { useForgeProjectSettingsModuleResource } from '@atlassian/jira-navigation-apps-resources';
import { toSections } from '@atlassian/jira-navigation-apps-sidebar-forge/src/common/utils';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

const getItemHref = (extension: ProjectSettingsPage, baseSettingsUrl: string) => {
	const { appId, envId } = parseExtensionId(extension.id);
	return `${baseSettingsUrl}/apps/${appId}/${envId}`;
};

export const useProjectSettingsForgeApps = (baseSettingsUrl: string) => {
	const { data: projectContext } = useProjectContext();
	const {
		data: forgeProjectSettingsData,
		loading,
		error,
	} = useForgeProjectSettingsModuleResource();
	const { isAdmin } = useTenantContext();
	const hasPermissions = projectContext?.isProjectAdmin || isAdmin;
	const doesExtensionMeetConditions = useDoesExtensionMeetConditions();

	const forgeApps = useMemo(() => {
		if (projectContext == null || forgeProjectSettingsData?.extensions == null || !hasPermissions) {
			return [];
		}

		return forgeProjectSettingsData.extensions
			.filter((extension) => doesExtensionMeetConditions(extension, projectContext))
			.map((extension) => ({
				id: extension.id,
				url: getItemHref(extension, baseSettingsUrl),
				name: extension.properties.title,
				iconUrl: extension.properties.icon,
				environmentType: extension.environmentType,
				environmentKey: extension.environmentKey,
				sections: toSections(extension),
			}));
	}, [
		baseSettingsUrl,
		doesExtensionMeetConditions,
		forgeProjectSettingsData,
		hasPermissions,
		projectContext,
	]);

	return { data: forgeApps, loading, error };
};
